'use client';

import { Component, type ErrorInfo, type ReactNode } from 'react';

import * as datadogRum from 'lib/telemetry/datadogRum';
import config from 'utils/config';
import { reloadPage } from 'utils/reloadPage';
import { getSessionStorageItem, setSessionStorageItem } from 'utils/storage';

export const CHUNK_SKEW_RELOAD_FLAG_KEY = 'chunk_skew_reloaded';

/**
 * Identifies the bundle currently running in this tab, so the reload guard is
 * scoped to the stale shell that failed rather than to the whole tab session.
 * `NEXT_PUBLIC_COMMIT_REF` is the deploy SHA (what Datadog reports as
 * `version`); when it is unset we fall back to the pages-router build id, then
 * to a constant — in which case the guard degrades to at most one reload per
 * tab session, the previous behaviour.
 */
export function getLoadedBuildId(): string {
    if (config.commitRef) {
        return config.commitRef;
    }
    const nextData = (globalThis as { __NEXT_DATA__?: { buildId?: string } }).__NEXT_DATA__;
    return nextData?.buildId || 'unknown';
}

/**
 * The `(0, mod.export)()` call shape bundlers emit for a *named import*. This is
 * the only "is not a function" TypeError we treat as version skew: the fresh
 * chunk calls an export that the stale shared module already in memory doesn't
 * have, so `mod.export` is undefined at call time.
 *
 *   Chrome:     "(0 , u.useFoo) is not a function"
 *   Safari/JSC: "(0,u.useFoo) is not a function. (In '(0,u.useFoo)()', '(0,u.useFoo)' is undefined)"
 *
 * Ordinary render bugs — a missing optional callback prop ("props.onClose is not
 * a function"), a non-function local — don't produce this shape, so they keep
 * propagating to the nearest parent boundary instead of forcing a reload.
 */
const STALE_EXPORT_CALL_PATTERN = /\(\s*0\s*,\s*[\w$]+(?:\.[\w$]+)+\s*\) is not a function/;

/**
 * Detects errors caused by version skew between the already-loaded bundle and
 * a lazily-fetched chunk from a newer deploy. A mismatched chunk loads
 * successfully — so importWithRetry's ChunkLoadError handling never fires —
 * and instead throws at render time when it calls an export that doesn't
 * exist in the older shared modules already in memory.
 */
export function isVersionSkewError(error: unknown): error is Error {
    if (!(error instanceof Error)) {
        return false;
    }
    if (error.name === 'ChunkLoadError') {
        return true;
    }
    return error.name === 'TypeError' && STALE_EXPORT_CALL_PATTERN.test(error.message);
}

interface ChunkSkewErrorBoundaryProps {
    children: ReactNode;
    /**
     * Rendered when a skew error is caught but this build already triggered an
     * auto-reload (so we don't reload again). Defaults to null.
     */
    fallback?: ReactNode;
}

interface ChunkSkewErrorBoundaryState {
    error: Error | null;
}

/**
 * Error boundary for lazily-loaded chunks that recovers from stale-bundle
 * version skew by reloading the page, mirroring the ChunkLoadError path in
 * `utils/importWithRetry`. To avoid a reload loop it reloads at most once per
 * loaded build id per tab session; a further skew error thrown by that same
 * stale shell renders `fallback` instead. Because the guard records the build
 * that failed, a tab that survives a later deploy can still recover from the
 * new skew. If the guard can't be persisted at all it renders `fallback` rather
 * than risk an unbounded reload loop.
 *
 * Non-skew errors — including ordinary `is not a function` render bugs that
 * don't match the stale-export call shape — are rethrown during render so they
 * propagate to the nearest parent boundary unchanged.
 */
export class ChunkSkewErrorBoundary extends Component<ChunkSkewErrorBoundaryProps, ChunkSkewErrorBoundaryState> {
    constructor(props: ChunkSkewErrorBoundaryProps) {
        super(props);
        this.state = { error: null };
    }

    static getDerivedStateFromError(error: Error): ChunkSkewErrorBoundaryState {
        return { error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        if (!isVersionSkewError(error)) {
            // render() rethrows so the nearest parent boundary handles it.
            return;
        }

        // Scoped to the build that failed: a reload that lands on a fresh build
        // no longer matches the stored id, so a skew from a later deploy in the
        // same tab can still recover.
        const loadedBuildId = getLoadedBuildId();
        const alreadyReloaded = getSessionStorageItem(CHUNK_SKEW_RELOAD_FLAG_KEY) === loadedBuildId;

        try {
            datadogRum.addError(error, {
                source: 'chunk-skew-error-boundary',
                componentStack: errorInfo.componentStack ?? undefined,
                alreadyReloaded,
                loadedBuildId,
            });
        } catch {
            // no-op: reporting should never crash the error boundary
        }

        if (alreadyReloaded) {
            console.error(
                '[ChunkSkewErrorBoundary] Version-skew error persisted after reload - rendering fallback',
                error,
            );
            return;
        }

        // Web Storage no-ops in sandboxed webviews and in Safari with cookies
        // blocked. An unpersisted guard would let the reloaded page hit the same
        // error and reload again forever, so only reload once we can read the
        // guard back; otherwise render the fallback.
        setSessionStorageItem(CHUNK_SKEW_RELOAD_FLAG_KEY, loadedBuildId);
        if (getSessionStorageItem(CHUNK_SKEW_RELOAD_FLAG_KEY) !== loadedBuildId) {
            console.error(
                '[ChunkSkewErrorBoundary] Version-skew reload guard could not be persisted - rendering fallback instead of reloading',
                error,
            );
            return;
        }

        console.warn('[ChunkSkewErrorBoundary] Version-skew error detected - reloading page', error);
        reloadPage();
    }

    render(): ReactNode {
        const { error } = this.state;
        if (error !== null) {
            if (!isVersionSkewError(error)) {
                throw error;
            }
            return this.props.fallback ?? null;
        }

        return this.props.children;
    }
}

export default ChunkSkewErrorBoundary;
